import { update } from '../utility/reload'

// 🛡️ CRITICAL USER INPUT ROUTES - Never interrupt these pages with version updates
//
// PREFERRED METHOD: Add to page component/definePageMeta:
//   definePageMeta({ blockVersionUpdate: true })
//   OR @Component({ meta: { blockVersionUpdate: true } })
//
// FALLBACK METHOD: Add route names to this list (for legacy pages)
const CRITICAL_USER_INPUT_ROUTES = [
  // 'reviews-post-review-campsite-campsiteSlug', // Post review page (uses meta instead)
  'booking-payment-paymentId-bookingId', // Payment processing
  // Add other critical user input routes as needed
]

// 🧪 DEBUG FLAG: Set to true to simulate version mismatches for testing
const FORCE_VERSION_MISMATCH = false

// 🧪 SECRET QUERY PARAMETER: For testers to simulate version upgrades
// Add ?forceVersionUpdate to any URL - parameter is ignored in version logic but triggers testing
// TESTING INSTRUCTIONS:
// 1. Visit /reviews/post-review/campsite/any-campsite/?verify=test&forceVersionUpdate
// 2. Should see "✅ PROTECTED" message (no update) - parameter visible but filtered out
// 3. Visit /any-other-page?forceVersionUpdate
// 4. Should see "🔄 UPDATING" and page refreshes - parameter filtered out of logic

export default defineNuxtPlugin(async (nuxtApp) => {
  const { $gtm } = nuxtApp
  const router = useRouter()
  const alfredVersion = nuxtApp.$config.public.alfredVersion
  console.log(`Alfred version: ${alfredVersion}`)

  // Check for secret test parameter and optionally remove it
  const hasForceVersionTest = () => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has('forceVersionUpdate')
  }

  // 🧪 TEST: Try to remove forceVersionUpdate parameter from URL
  const tryRemoveForceVersionParam = () => {
    if (!hasForceVersionTest()) return false

    try {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.delete('forceVersionUpdate')

      // Use history.replaceState to update URL
      window.history.replaceState(
        window.history.state,
        document.title,
        currentUrl.toString(),
      )

      return true
    } catch (e) {
      console.error('[version-upgrade] Error removing test parameter:', e)
      return false
    }
  }

  if (FORCE_VERSION_MISMATCH) {
    console.warn(
      '🧪 DEBUG MODE: Version mismatch testing enabled - all routes will trigger version checks',
    )
  }

  if (hasForceVersionTest()) {
    console.warn(
      '🧪 TESTER MODE: forceVersionUpdate parameter detected - simulating version mismatch',
    )

    // Try to remove the parameter from URL for cleaner testing
    setTimeout(() => {
      tryRemoveForceVersionParam()
    }, 1000) // Wait a bit for all plugins to load
  }

  let version: string
  try {
    // Fetch the version information from the server
    ;({ version } = await $fetch('/api/version/', {
      timeout: 10000,
    }))
  } catch (error) {
    return
  }

  // Key for storing upgrade attempt status in LocalStorage
  const upgradeKey = `upgrade-attempt-${version}`
  const versionMismatchKey = `version-mismatch-${version}`

  // Clean up old upgrade keys from previous versions (prevent localStorage pollution)
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key &&
        (key.startsWith('upgrade-attempt-') ||
          key.startsWith('version-mismatch-')) &&
        !key.includes(version)
      ) {
        localStorage.removeItem(key)
      }
    }
  } catch (error) {
    // Ignore cleanup errors
  }

  // Compare the fetched version with the current environment version
  const hasVersionMismatch =
    FORCE_VERSION_MISMATCH || hasForceVersionTest() || version !== alfredVersion

  // IMPORTANT: Check version match FIRST (before checking failed attempt)
  // If versions match, we had a successful upgrade - clear any old flags
  if (!hasVersionMismatch) {
    const hadFailedAttempt = localStorage.getItem(upgradeKey)
    if (hadFailedAttempt) {
      console.log(
        `✅ Version upgrade successful (${version}). Clearing failed attempt flag.`,
      )
      localStorage.removeItem(upgradeKey)
    }
    // Also clear version mismatch flag if it exists
    localStorage.removeItem(versionMismatchKey)
    return
  }

  // Check if an upgrade attempt failed previously
  // This prevents infinite reload loops when there's a persistent version mismatch
  // (e.g., CDN caching issues, gradual rollout, browser cache)
  const failedAttempt = localStorage.getItem(upgradeKey)
  if (failedAttempt) {
    console.warn(
      `Previous upgrade attempt for version ${version} failed. Skipping reload to prevent infinite loop.`,
    )
    return
  }

  if (hasVersionMismatch) {
    if (FORCE_VERSION_MISMATCH) {
      console.warn(
        '🧪 DEBUG: Forcing version mismatch for testing (constant)',
        version,
        alfredVersion,
      )
    } else if (hasForceVersionTest()) {
      console.warn(
        '🧪 TESTER: Forcing version mismatch via secret parameter',
        version,
        alfredVersion,
      )
    } else {
      console.warn('Alfred version mismatch.', version, alfredVersion)
    }

    // Store version mismatch in localStorage
    localStorage.setItem(versionMismatchKey, 'true')

    // Track version mismatch event
    if ($gtm && typeof ($gtm as any).push === 'function') {
      ;($gtm as any).push({
        event: 'version-mismatch',
        version,
        alfredVersion,
      })
    }

    // Function to check and perform update if needed
    const checkAndUpdate = (route: typeof router.currentRoute.value) => {
      // Filter out our test parameter when checking for query params
      const relevantQueryParams = Object.keys(route.query).filter(
        (key) => key !== 'forceVersionUpdate',
      )

      // Check route meta first (preferred method)
      if (route.meta?.blockVersionUpdate) {
        if (hasForceVersionTest()) {
          console.log(
            `✅ TESTER: Page protected from version update (${String(route.name)})`,
          )
        }
        return
      }

      // Fallback to route name list for legacy routes
      if (
        route.name &&
        CRITICAL_USER_INPUT_ROUTES.includes(String(route.name))
      ) {
        if (hasForceVersionTest()) {
          console.log(
            `✅ TESTER: Page protected from version update (${String(route.name)})`,
          )
        }
        return
      }

      const hasQueryParams = relevantQueryParams.length > 0
      const versionMismatch = localStorage.getItem(versionMismatchKey)

      if (!hasQueryParams && versionMismatch) {
        console.warn('🔄 Version update: Refreshing page...')
        // Set flag to prevent reload loops if upgrade fails
        localStorage.setItem(upgradeKey, 'true')
        localStorage.removeItem(versionMismatchKey)
        update()
      } else if (hasQueryParams && versionMismatch) {
        console.log(
          `⏳ Version update: Waiting for clean URL (${relevantQueryParams.length} params)`,
        )
      }
    }

    // Check on initial route
    checkAndUpdate(router.currentRoute.value)

    // Add route change handler
    router.afterEach((to: typeof router.currentRoute.value) => {
      checkAndUpdate(to)
    })
  }
})
